import { assign, createMachine } from 'xstate';
import type { TrialEvent, TrialPlan } from './types';

export interface TrialMachineContext {
  plan: TrialPlan;
  stimulusStart: number;
  stimulusEnd: number;
  holdStart: number;
  reproducedMs: number;
  flowScore: number;
  events: TrialEvent[];
}

type TrialMachineEvent =
  | { type: 'PRIME' }
  | { type: 'STIMULUS_STARTED'; at: number }
  | { type: 'STIMULUS_DONE'; at: number }
  | { type: 'HOLD_STARTED'; at: number }
  | { type: 'HOLD_ENDED'; at: number }
  | { type: 'FLOW_HIT'; at: number; value: number }
  | { type: 'SUBMIT_REPORT' }
  | { type: 'RESET' };

export function createTrialMachine(plan: TrialPlan) {
  return createMachine({
    id: `trial-${plan.id}`,
    initial: 'prime',
    types: {} as { context: TrialMachineContext; events: TrialMachineEvent },
    context: {
      plan,
      stimulusStart: 0,
      stimulusEnd: 0,
      holdStart: 0,
      reproducedMs: 0,
      flowScore: 0,
      events: []
    },
    states: {
      prime: {
        on: {
          PRIME: 'stimulus'
        }
      },
      stimulus: {
        entry: assign({
          events: ({ context }) => [...context.events, { t: performance.now(), type: 'stimulus.visible' }]
        }),
        on: {
          STIMULUS_STARTED: {
            actions: assign({
              stimulusStart: ({ event }) => event.at,
              events: ({ context, event }) => [...context.events, { t: event.at, type: 'stimulus.start' }]
            })
          },
          FLOW_HIT: {
            actions: assign({
              flowScore: ({ event }) => event.value,
              events: ({ context, event }) => [...context.events, { t: event.at, type: 'flow.hit', value: event.value }]
            })
          },
          STIMULUS_DONE: {
            target: 'reproduce',
            actions: assign({
              stimulusEnd: ({ event }) => event.at,
              events: ({ context, event }) => [...context.events, { t: event.at, type: 'stimulus.end' }]
            })
          }
        }
      },
      reproduce: {
        on: {
          HOLD_STARTED: {
            actions: assign({
              holdStart: ({ event }) => event.at,
              events: ({ context, event }) => [...context.events, { t: event.at, type: 'hold.start' }]
            })
          },
          HOLD_ENDED: {
            target: 'report',
            actions: assign({
              reproducedMs: ({ context, event }) => Math.max(0, event.at - context.holdStart),
              events: ({ context, event }) => [...context.events, { t: event.at, type: 'hold.end' }]
            })
          }
        }
      },
      report: {
        on: {
          SUBMIT_REPORT: 'done'
        }
      },
      done: { type: 'final' }
    }
  });
}
